import React from 'react';
import { clsx } from 'clsx';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  glow?: boolean;
}

export function Button({
  children,
  variant = 'primary',
  size = 'md',
  loading = false,
  glow = false,
  className,
  disabled,
  ...props
}: ButtonProps) {
  const baseStyles = clsx(
    'relative font-semibold rounded-xl transition-all duration-300',
    'inline-flex items-center justify-center gap-2',
    'focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:ring-offset-2 focus:ring-offset-slate-900',
    'disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none'
  );

  const variants = {
    primary: clsx(
      'bg-gradient-to-r from-cyan-500 via-purple-500 to-pink-500',
      'text-white font-bold tracking-wide',
      'hover:shadow-glow hover:scale-[1.02]',
      'active:scale-[0.98]',
      glow && 'shadow-glow animate-pulse-slow'
    ),
    secondary: clsx(
      'bg-slate-800/80 backdrop-blur-sm',
      'border border-slate-700/50 hover:border-cyan-500/30',
      'text-slate-200 hover:text-white',
      'hover:bg-slate-800 hover:shadow-glow-sm'
    ),
    ghost: clsx(
      'bg-transparent hover:bg-slate-800/50',
      'text-slate-400 hover:text-cyan-400',
      'border border-transparent hover:border-slate-700/50'
    ),
    danger: clsx(
      'bg-gradient-to-r from-red-600 to-rose-600',
      'text-white font-bold',
      'hover:from-red-500 hover:to-rose-500',
      'hover:shadow-[0_0_30px_rgba(239,68,68,0.3)]'
    ),
  };

  const sizes = {
    sm: 'py-2 px-4 text-sm',
    md: 'py-3 px-6 text-base',
    lg: 'py-4 px-8 text-lg',
  };

  return (
    <button
      className={clsx(
        baseStyles,
        variants[variant],
        sizes[size],
        className
      )}
      disabled={disabled || loading}
      {...props}
    >
      {loading && (
        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="3"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
          />
        </svg>
      )}
      {children}
    </button>
  );
}
